import { createAction, createReducer } from '@reduxjs/toolkit';

import {
    Common,
    LayerManager,
    LocationManager,
    Loadable,
    LegendManager,
    IdentifyManager,
} from 'models';
import { makeThunkFromAPICall } from 'state';

import { FeatureLayers } from 'components/map/FeaturePopup';
import { LegendModel } from 'models/legend';

export type LayerState = {
    layerConfigs: Loadable<LayerManager.LayerConfig[]>;
    layerRestrictions: Loadable<LayerManager.LayerRestriction[]>;
    layerMetadata: Loadable<LayerManager.LayerMetadata[]>;
    updateLayerMetadataResponse: Loadable<LayerManager.LayerMetadata>;
    legends: Record<LayerManager.Layer.Id, Loadable<LegendModel.Legend>>;
    bookmarkedLayers: Loadable<LayerManager.LayerConfig[]>;
    bookmarkUpdate: Loadable<null>;
    selectedBaseMap: LayerManager.Layer.Id | null;
    selectedLayers: LayerManager.Layer.Id[] | null;
    selectedLegendLayer: LayerManager.Layer.Id | null;
    viewBounds: Common.Bounds | null;
    locations: Loadable<LocationManager.Location[]>;
    sendAddLocationResponse: Loadable<LocationManager.API.ActionResponse>;
    sendRemoveLocationResponse: Loadable<LocationManager.API.ActionResponse>;
    identifyPopupResults: FeatureLayers[];
};

const initialState: LayerState = {
    layerConfigs: {
        status: 'idle',
    },
    layerRestrictions: {
        status: 'idle',
    },
    layerMetadata: {
        status: 'idle',
    },
    updateLayerMetadataResponse: {
        status: 'idle',
    },
    legends: LayerManager.layerIds.reduce<
        Record<LayerManager.Layer.Id, Loadable<LegendModel.Legend>>
    >(
        (obj, id) => {
            obj[id] = { status: 'idle' };
            return obj;
        },
        {} as Record<LayerManager.Layer.Id, Loadable<LegendModel.Legend>>,
    ),
    bookmarkedLayers: {
        status: 'idle',
    },
    bookmarkUpdate: {
        status: 'idle',
    },
    selectedLayers: [],
    selectedBaseMap: 'OSM',
    selectedLegendLayer: null,
    viewBounds: null,
    locations: {
        status: 'idle',
    },
    sendAddLocationResponse: {
        status: 'idle',
    },
    sendRemoveLocationResponse: {
        status: 'idle',
    },
    identifyPopupResults: [],
};

export const setViewBounds = createAction<Common.Bounds | null>(
    'layers/viewbounds/set',
);

export const setBasemapLayer = createAction<LayerManager.Layer.Id | null>(
    'layers/selected/basemap',
);

export const setLegendLayer = createAction<LayerManager.Layer.Id | null>(
    'layers/selected/legend',
);

export const setSelectedLayers = createAction<LayerManager.Layer.Id[] | null>(
    'layers/selected/layers',
);

export const setCommentsNull = createAction('layers/selected/comments/null');

export const setIdentifyPopupResults = createAction<
    LayerState['identifyPopupResults']
>('layers/identify/set');

export const [getLayers, setLayers] = makeThunkFromAPICall(
    LayerManager.getLayers,
    'layers/set',
);

export const [getLayerRestrictions, setLayerRestrictions] =
    makeThunkFromAPICall(
        LayerManager.getLayerRestrictions,
        'layers/restrictions/get',
    );

export const [getLayerMetadata, setLayerMetadata] = makeThunkFromAPICall(
    LayerManager.getLayerMetadata,
    'layers/metadata/get',
);

export const [updateLayerMetadata, setUpdateLayerMetadata] =
    makeThunkFromAPICall(
        LayerManager.updateLayerMetadata,
        'layers/metadata/update',
    );

export const [getBookmarkedLayers, setBookmarks] = makeThunkFromAPICall(
    LayerManager.getBookmarkedLayers,
    'layers/bookmarks/set',
    false,
);

export const [addBookmark, addBookmarkStatus] = makeThunkFromAPICall(
    LayerManager.addBookmark,
    'layers/bookmarks/add',
    false,
);

export const [removeBookmark, removeBookmarkStatus] = makeThunkFromAPICall(
    LayerManager.removeBookmark,
    'layers/bookmarks/remove',
    false,
);

export const [clearBookmarks, clearBookmarksStatus] = makeThunkFromAPICall(
    LayerManager.clearBookmarks,
    'layers/bookmarks/clear',
    false,
);

export const [identifyLayers, setIdentifyLayers] = makeThunkFromAPICall(
    IdentifyManager.identifyFeatures,
    'layers/identify',
);

export const [getLegend, setLegend] = makeThunkFromAPICall(
    LegendManager.getLegend,
    'layers/legend/set',
);

// export const [getGeoserverLegendImage, setGeoserverLegendImage] =
//     makeThunkFromAPICall(
//         GeoserverManager.getLegendImage,
//         'layers/geoserver/legendImage/set',
//     );

// export const [queryGeoserverWMSImage, setGeoserverWMSQueryImage] =
//     makeThunkFromAPICall(
//         GeoserverManager.queryWMSImage,
//         'layers/geoserver/wmsQueryImage/set',
//     );

export const [getLocations, setLocations] = makeThunkFromAPICall(
    LocationManager.getLocations,
    'locations/set',
);

export const [sendAddLocation, setAddLocationResponse] = makeThunkFromAPICall(
    LocationManager.addLocation,
    'locations/add',
);

export const [sendRemoveLocation, setRemoveLocationResponse] =
    makeThunkFromAPICall(LocationManager.removeLocation, 'locations/remove');

export const layerReducer = createReducer(initialState, (builder) => {
    builder.addCase(setLayers, (state, action) => {
        return {
            ...state,
            layerConfigs: { ...state.layerConfigs, ...action.payload.loadable },
        };
    });

    builder.addCase(setLayerRestrictions, (state, action) => {
        return {
            ...state,
            layerRestrictions: {
                ...state.layerRestrictions,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setLayerMetadata, (state, action) => {
        return {
            ...state,
            layerMetadata: {
                ...state.layerMetadata,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setUpdateLayerMetadata, (state, action) => {
        return {
            ...state,
            updateLayerMetadataResponse: {
                ...state.updateLayerMetadataResponse,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setBookmarks, (state, action) => {
        return {
            ...state,
            bookmarkedLayers: {
                ...state.bookmarkedLayers,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(addBookmarkStatus, (state, action) => {
        return {
            ...state,
            bookmarkUpdate: {
                ...state.bookmarkUpdate,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setIdentifyPopupResults, (state, action) => {
        return {
            ...state,
            identifyPopupResults: action.payload,
        };
    });

    builder.addCase(removeBookmarkStatus, (state, action) => {
        return {
            ...state,
            bookmarkUpdate: {
                ...state.bookmarkUpdate,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(clearBookmarksStatus, (state, action) => {
        return {
            ...state,
            bookmarkUpdate: {
                ...state.bookmarkUpdate,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setSelectedLayers, (state, action) => {
        return { ...state, selectedLayers: action.payload };
    });

    builder.addCase(setBasemapLayer, (state, action) => {
        return { ...state, selectedBaseMap: action.payload };
    });

    builder.addCase(setLegendLayer, (state, action) => {
        return { ...state, selectedLegendLayer: action.payload };
    });

    builder.addCase(setLegend, (state, action) => {
        return {
            ...state,
            legends: {
                ...state.legends,
                [action.payload.input.config.id]: {
                    ...state.legends[action.payload.input.config.id],
                    ...action.payload.loadable,
                },
            },
        };
    });

    builder.addCase(setViewBounds, (state, action) => {
        return { ...state, viewBounds: action.payload };
    });

    builder.addCase(setLocations, (state, action) => {
        return {
            ...state,
            locations: { ...state.locations, ...action.payload.loadable },
        };
    });

    builder.addCase(setAddLocationResponse, (state, action) => {
        return {
            ...state,
            sendAddLocationResponse: {
                ...state.sendAddLocationResponse,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setRemoveLocationResponse, (state, action) => {
        return {
            ...state,
            sendRemoveLocationResponse: {
                ...state.sendRemoveLocationResponse,
                ...action.payload.loadable,
            },
        };
    });

    builder.addCase(setCommentsNull, (state) => {
        return {
            ...state,
            selectedLayers: null,
        };
    });
});

export const LayerActions = {
    getLayers,
    getBookmarkedLayers,
    getLayerRestrictions,
    getLayerMetadata,
    updateLayerMetadata,
    setBookmarks,
    setSelectedLayers,
    setCommentsNull,
    setBasemapLayer,
    setLegendLayer,
    setIdentifyPopupResults,
    addBookmark,
    removeBookmark,
    clearBookmarks,
    identifyLayers,
    getLegend,
    setLegend,
    setViewBounds,
};
export default layerReducer;
